import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Toaster, toast } from "sonner";
import {
  Lightning,
  Truck,
  CurrencyCircleDollar,
  ChartLineUp,
  Image as ImageIcon,
  UploadSimple,
  Calculator,
} from "@phosphor-icons/react";

import { PUBLIC_BACKEND as BACKEND } from "@/lib/api";

export default function ZipExpressSettings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const [previewDist, setPreviewDist] = useState(8);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/zipexpress/settings");
      setS(data);
      setForm({
        base_flat_fee: data.base_flat_fee,
        per_km_rate: data.per_km_rate,
        over_max_per_km_rate: data.over_max_per_km_rate,
        currency: data.currency,
        driver_share_pct: data.driver_share_pct,
        zipexpress_share_pct: data.zipexpress_share_pct,
        is_active: data.is_active,
      });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    }
  };

  useEffect(() => { load(); }, []);

  // Live fee preview
  useEffect(() => {
    if (!form) return;
    const t = setTimeout(() => {
      api.get(`/zipexpress/calculate-fee?distance_km=${previewDist}`).then((r) => setPreview(r.data)).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [previewDist, form, s]);

  const updateField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const updateDriverShare = (val) => {
    const v = Math.max(0, Math.min(100, parseInt(val) || 0));
    setForm((f) => ({ ...f, driver_share_pct: v, zipexpress_share_pct: 100 - v }));
  };
  const updateZipxShare = (val) => {
    const v = Math.max(0, Math.min(100, parseInt(val) || 0));
    setForm((f) => ({ ...f, zipexpress_share_pct: v, driver_share_pct: 100 - v }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        base_flat_fee: parseFloat(form.base_flat_fee),
        per_km_rate: parseFloat(form.per_km_rate),
        over_max_per_km_rate: parseFloat(form.over_max_per_km_rate),
        currency: form.currency,
        driver_share_pct: parseInt(form.driver_share_pct),
        zipexpress_share_pct: parseInt(form.zipexpress_share_pct),
        is_active: form.is_active,
      };
      const { data } = await api.put("/zipexpress/settings", payload);
      setS(data);
      toast.success("ZipExpress settings updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/zipexpress/settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setS(data);
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  if (!s || !form) return <div className="p-8 text-zinc-500" data-testid="zipx-loading">Loading…</div>;

  const logoUrl = s.logo_path ? `${BACKEND}/api/zipexpress/logo?v=${s.updated_at}` : null;

  return (
    <div className="p-6 sm:p-8 max-w-[1200px]" data-testid="zipexpress-settings-page">
      <Toaster position="top-right" />

      <div className="mb-8 flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-4">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500 dark:text-zinc-400 flex items-center gap-1.5">
            <Lightning size={12} weight="bold" /> Last-mile partner
          </div>
          <h1 className="text-3xl font-semibold tracking-tight mt-1 text-zinc-900 dark:text-zinc-100" style={{ fontFamily: "Work Sans" }}>
            ZipExpress
          </h1>
          <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">
            {s.tagline}. <a href={s.website} target="_blank" rel="noreferrer" className="text-[#002BF6] hover:underline">{s.website}</a>
          </p>
        </div>
        <Link to="/zipexpress/revenue">
          <Button variant="outline" data-testid="zipx-revenue-btn">
            <ChartLineUp size={14} weight="bold" className="mr-1.5" /> Revenue report
          </Button>
        </Link>
      </div>

      {/* Brand strip */}
      <div className="mb-8 rounded-md border border-zinc-200 dark:border-zinc-800 bg-gradient-to-br from-zinc-50 to-white dark:from-zinc-900/40 dark:to-zinc-900 p-6 flex items-center gap-5 flex-wrap" data-testid="zipx-brand-strip">
        <div className="w-20 h-20 rounded-md bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 flex items-center justify-center overflow-hidden">
          {logoUrl ? (
            <img src={logoUrl} alt="ZipExpress" className="w-full h-full object-contain" data-testid="zipx-current-logo" />
          ) : (
            <span className="text-xs text-zinc-400">No logo</span>
          )}
        </div>
        <div className="flex-1 min-w-[200px]">
          <div className="text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-100" style={{ fontFamily: "Work Sans" }}>{s.brand_name}</div>
          <div className="text-xs text-zinc-500 mt-0.5">Exclusive last-mile partner · Powered by Kanal</div>
        </div>
        <label className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md border border-zinc-300 dark:border-zinc-700 text-sm font-medium cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-800" data-testid="zipx-upload-logo-label">
          <UploadSimple size={14} weight="bold" />
          {uploading ? "Uploading…" : (s.logo_path ? "Replace logo" : "Upload logo")}
          <input type="file" accept="image/*" hidden onChange={uploadLogo} disabled={uploading} data-testid="zipx-upload-logo-input" />
        </label>
      </div>

      {/* Rate config */}
      <div className="grid md:grid-cols-[1.4fr_1fr] gap-6 mb-6">
        <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-md p-6" data-testid="zipx-rate-card">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-8 h-8 rounded-md bg-[#002BF6] text-white flex items-center justify-center"><CurrencyCircleDollar size={15} weight="bold" /></div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Delivery rate</div>
              <div className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">Round-town &amp; extended distance</div>
            </div>
          </div>

          <div className="bg-blue-50 dark:bg-blue-500/10 border border-blue-200 dark:border-blue-500/30 rounded text-xs text-blue-900 dark:text-blue-200 p-3 mb-5">
            <b>Pricing formula:</b> total = base_flat_fee + (per_km_rate × min(distance, 14km)) + (over_14km_per_km_rate × max(0, distance − 14km)).
            Round-town distance is <b>1–14 km</b>.
          </div>

          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            <RateField label="Base flat fee" testid="zipx-base-flat-fee" suffix={form.currency} value={form.base_flat_fee} onChange={(v) => updateField("base_flat_fee", v)} />
            <RateField label="Per-km rate (1-14 km)" testid="zipx-per-km-rate" suffix={`${form.currency}/km`} value={form.per_km_rate} onChange={(v) => updateField("per_km_rate", v)} />
            <RateField label="Per-km rate (over 14 km)" testid="zipx-over-rate" suffix={`${form.currency}/km`} value={form.over_max_per_km_rate} onChange={(v) => updateField("over_max_per_km_rate", v)} />
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Currency</Label>
              <select
                value={form.currency}
                onChange={(e) => updateField("currency", e.target.value)}
                data-testid="zipx-currency"
                className="mt-1.5 w-full h-9 px-3 border border-zinc-200 dark:border-zinc-800 rounded bg-white dark:bg-zinc-900 text-sm"
              >
                <option value="JMD">JMD</option>
                <option value="USD">USD</option>
              </select>
            </div>
          </div>
        </div>

        {/* Live preview */}
        <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-md p-6" data-testid="zipx-preview-card">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-8 h-8 rounded-md bg-emerald-500 text-white flex items-center justify-center"><Calculator size={15} weight="bold" /></div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Fee calculator</div>
              <div className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">Live preview</div>
            </div>
          </div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">Try a distance (km)</Label>
          <Input type="number" step="0.5" min="0" className="mt-1.5 mb-4" value={previewDist} onChange={(e) => setPreviewDist(parseFloat(e.target.value) || 0)} data-testid="zipx-preview-dist-input" />
          {preview && (
            <div className="space-y-2 text-sm" data-testid="zipx-preview-result">
              <PreviewRow label="Distance" value={`${preview.distance_km.toFixed(1)} km`} />
              <PreviewRow label="In-base portion" value={`${preview.in_base_km.toFixed(1)} km`} />
              <PreviewRow label="Overage portion" value={`${preview.overage_km.toFixed(1)} km`} muted={preview.overage_km === 0} />
              <div className="h-px bg-zinc-200 dark:bg-zinc-800 my-3" />
              <PreviewRow label="Total fee" value={<b>{preview.currency} ${preview.total.toLocaleString()}</b>} />
              <PreviewRow label={`Driver (${preview.driver_share_pct}%)`} value={<span className="text-emerald-700 dark:text-emerald-300 font-semibold">{preview.currency} ${preview.driver_cut.toLocaleString()}</span>} testid="zipx-preview-driver-cut" />
              <PreviewRow label={`ZipExpress (${preview.zipexpress_share_pct}%)`} value={<span className="text-[#002BF6] font-semibold">{preview.currency} ${preview.zipexpress_cut.toLocaleString()}</span>} testid="zipx-preview-zipx-cut" />
            </div>
          )}
        </div>
      </div>

      {/* Revenue share */}
      <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-md p-6 mb-6" data-testid="zipx-revshare-card">
        <div className="flex items-center gap-2 mb-5">
          <div className="w-8 h-8 rounded-md bg-amber-500 text-white flex items-center justify-center"><ChartLineUp size={15} weight="bold" /></div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Revenue share</div>
            <div className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">Driver / ZipExpress split</div>
          </div>
          <div className="ml-auto text-xs text-zinc-500">Auto-balances to 100%</div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600 flex items-center gap-1.5"><Truck size={11} weight="bold" /> Driver share %</Label>
            <div className="mt-1.5 relative">
              <Input type="number" min="0" max="100" value={form.driver_share_pct} onChange={(e) => updateDriverShare(e.target.value)} data-testid="zipx-driver-share" className="no-spinner pr-10" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-zinc-500 font-medium pointer-events-none">%</span>
            </div>
          </div>
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600 flex items-center gap-1.5"><Lightning size={11} weight="bold" /> ZipExpress share %</Label>
            <div className="mt-1.5 relative">
              <Input type="number" min="0" max="100" value={form.zipexpress_share_pct} onChange={(e) => updateZipxShare(e.target.value)} data-testid="zipx-zipx-share" className="no-spinner pr-10" />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-zinc-500 font-medium pointer-events-none">%</span>
            </div>
          </div>
        </div>
        <div className="mt-4 flex h-3 rounded-full overflow-hidden bg-zinc-100 dark:bg-zinc-800" data-testid="zipx-share-bar">
          <div className="bg-emerald-500 transition-all" style={{ width: `${form.driver_share_pct}%` }} />
          <div className="bg-[#002BF6] transition-all" style={{ width: `${form.zipexpress_share_pct}%` }} />
        </div>
        <div className="flex justify-between mt-1.5 text-[11px] text-zinc-500">
          <span><span className="inline-block w-2.5 h-2.5 rounded-full bg-emerald-500 mr-1.5 align-middle" />Driver {form.driver_share_pct}%</span>
          <span><span className="inline-block w-2.5 h-2.5 rounded-full bg-[#002BF6] mr-1.5 align-middle" />ZipExpress {form.zipexpress_share_pct}%</span>
        </div>
      </div>

      {/* Save */}
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={load} disabled={saving} data-testid="zipx-reset-btn">Discard</Button>
        <Button onClick={save} disabled={saving} className="bg-[#002BF6] hover:bg-[#0021C7] text-white" data-testid="zipx-save-btn">
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </div>

      <p className="text-[11px] text-zinc-500 mt-4">
        Changes take effect immediately for new delivery requests. Active jobs are not retroactively re-priced.
      </p>
    </div>
  );
}

function RateField({ label, value, onChange, testid, suffix }) {
  return (
    <div>
      <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-600">{label}</Label>
      <div className="mt-1.5 relative">
        <Input type="number" step="0.01" min="0" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className={`no-spinner ${suffix ? "pr-16" : ""}`} />
        {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-zinc-500 font-medium pointer-events-none">{suffix}</span>}
      </div>
    </div>
  );
}

function PreviewRow({ label, value, muted, testid }) {
  return (
    <div className="flex items-center justify-between" data-testid={testid}>
      <span className={`text-xs ${muted ? "text-zinc-400" : "text-zinc-600 dark:text-zinc-400"}`}>{label}</span>
      <span className={`text-sm ${muted ? "text-zinc-400" : "text-zinc-900 dark:text-zinc-100"}`}>{value}</span>
    </div>
  );
}
